import React, { useCallback, useMemo, useState } from "react";
import { Autocomplete, CustomTextField, LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { Close, Search } from "@material-ui/icons";
import {
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  makeStyles,
  Paper,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { onTextBoxChange } from "utils/propertyChangeAdapters";
import { get, set } from "@totalsoft/change-tracking-react";
import PropTypes from "prop-types";
import { QuoteActionRequiredByList, QuoteOrderByList } from "../constants";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { RESPONSIBLE_QUERY } from "../queries";
import { emptyString, emptyArray } from "constants/defaultValues";
import ActionRequiredByOption from "./ActionRequiredByOption";
import useUserData from "hooks/useUserData";
import { getRightToChangeResponsible } from "features/common/rights";

const useStyles = makeStyles((_theme) => ({
  filterStyle: { padding: "10px" },
  listInfo: { textAlign: "end", paddingRight: "20px", marginTop: "10px" },
}));

function QuoteListFilters({ quoteList, filter, filterLens, resetFullListOnSearch, loadingList }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [fullTextSearch, setFullTextSearch] = useState(filter.keyword);
  const orderBy = QuoteOrderByList.map((x) => ({ ...x, name: t(x.name) }));

  const { data: responsible, loading: loadingResponsibles } = useQueryWithErrorHandling(RESPONSIBLE_QUERY);

  const onPropertyChange = useCallback(
    () => (value) => {
      setFullTextSearch(value);
    },
    [setFullTextSearch]
  );

  const handleFullTextSearchReset = useCallback(() => {
    setFullTextSearch(emptyString);
    resetFullListOnSearch();
    (filterLens.keyword |> set)(null);
  }, [filterLens, setFullTextSearch, resetFullListOnSearch]);

  const handleSearch = useCallback(() => {
    if (loadingList) return;
    (filterLens.keyword |> set)(fullTextSearch);
    resetFullListOnSearch();
  }, [fullTextSearch, filterLens, resetFullListOnSearch, loadingList]);

  const handleKeypress = useCallback(
    (e) => {
      if (e.keyCode === 13) {
        handleSearch();
      }
      if (e.keyCode === 46) {
        handleFullTextSearchReset();
      }
    },
    [handleSearch, handleFullTextSearchReset]
  );

  const handleResponsibleChange = useCallback(
    (value) => {
      (filterLens.responsibleId |> set)(value);
      resetFullListOnSearch();
    },
    [filterLens.responsibleId, resetFullListOnSearch]
  );

  const handleActionRequiredByChange = useCallback(
    (value) => {
      (filterLens.requiredById |> set)(value);
      resetFullListOnSearch();
    },
    [filterLens.requiredById, resetFullListOnSearch]
  );

  const handleOrderByIdChange = useCallback(
    (value) => {
      (filterLens.orderById |> set)(value);
      resetFullListOnSearch();
    },
    [filterLens.orderById, resetFullListOnSearch]
  );

  const { userRights } = useUserData();
  const hasRightToChangeResponsible = useMemo(() => getRightToChangeResponsible(userRights), [userRights]);

  return loadingResponsibles ? (
    <LoadingFakeText lines={2} />
  ) : (
    <Paper className={classes.filterStyle} onKeyUp={handleKeypress}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={4}>
          <CustomTextField
            id="fullTextSearchField"
            fullWidth
            label={t("General.Search")}
            value={fullTextSearch || emptyString}
            onChange={onTextBoxChange(onPropertyChange("fullTextSearch"))}
            endAdornment={
              fullTextSearch ? (
                <InputAdornment position="end">
                  <Tooltip title={t("General.ResetFilter")}>
                    <IconButton onClick={handleFullTextSearchReset}>
                      <Close />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : (
                <></>
              )
            }
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            id="docReq_nextBtn"
            right="true"
            variant="contained"
            color="primary"
            disabled={loadingList}
            onClick={handleSearch}
            startIcon={loadingList ? <CircularProgress size={20} /> : <Search />}
          >
            {t("General.Search")}
          </Button>
        </Grid>
        <Grid item container spacing={2} xs={12} sm={6} alignItems="center" justifyContent="flex-end">
          <Grid item xs={12} sm={userRights?.hasEstimatorOnlyRight ? 5 : 4}>
            {hasRightToChangeResponsible && (
              <Autocomplete
                id="responsibleId"
                value={filter.responsibleId || emptyString}
                label={t("Quote.Responsible")}
                simpleValue={true}
                options={responsible?.responsibleList || emptyArray}
                valueKey="id"
                labelKey="name"
                onChange={handleResponsibleChange}
                isClearable={true}
                noOptionsText={t("General.AutocompleteNoOptions")}
              />
            )}
          </Grid>
          {!userRights?.hasEstimatorOnlyRight && (
            <Grid item xs={12} sm={4}>
              <Autocomplete
                id="actionRequiredById"
                value={(filterLens.requiredById |> get) || emptyString}
                label={t("Quote.ActionRequiredBy")}
                simpleValue={true}
                options={QuoteActionRequiredByList.map((x) => ({ ...x, name: t(x.name) }))}
                valueKey="id"
                labelKey="name"
                onChange={handleActionRequiredByChange}
                isClearable={true}
                renderOption={ActionRequiredByOption}
                noOptionsText={t("General.AutocompleteNoOptions")}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={userRights?.hasEstimatorOnlyRight ? 5 : 4}>
            <Autocomplete
              id="orderById"
              value={(filterLens.orderById |> get) || emptyString}
              label={t("Quote.SortBy")}
              simpleValue={true}
              options={orderBy || emptyArray}
              valueKey="id"
              labelKey="name"
              onChange={handleOrderByIdChange}
              noOptionsText={t("General.AutocompleteNoOptions")}
            />
          </Grid>
        </Grid>
      </Grid>
      <div className={classes.listInfo}>
        {t("Quote.ListInfo", {
          quotes: quoteList.values.length,
          totalCount: quoteList.totalCount,
          page: filter.page,
          totalPages: quoteList.totalPages,
        })}
      </div>
    </Paper>
  );
}

QuoteListFilters.propTypes = {
  quoteList: PropTypes.object.isRequired,
  filter: PropTypes.object.isRequired,
  filterLens: PropTypes.object.isRequired,
  resetFullListOnSearch: PropTypes.func.isRequired,
  loadingList: PropTypes.bool.isRequired,
};

export default QuoteListFilters;
